import { diagnosticItems, diagnosticTagIds } from "@constants/mocked-data";
import { events } from "@constants/tracking";
import { useSession } from "@features/session/hooks/useSession";
import useAnalytics from "@hooks/useAnalytics";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { smartPluralize } from "@memorang/helpers/src/common";
import type { PracticeTestExam } from "@memorang/types/src/tag";
import Box from "@memorang/ui/src/components/Box";
import CustomChip from "@memorang/ui/src/components/CustomChip";
import type { IconType } from "@memorang/ui/src/types";

import DiagnosticCard from "@memorang/ui/src/components/DiagnosticCard";
import { MicrophonePermissionDialog } from "@memorang/ui/src/components/MicroPhonePermissionDialog";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import { Audio } from "expo-av";
import { router } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";

type Props = {
	exams?: PracticeTestExam[];
	hasAccess?: boolean;
	handleShowPurchase: () => void;
	paddingHorizontal?: number;
	handleClose?: () => void;
	isSessionConfig?: boolean;
	isDataLoading?: boolean;
	disableAll?: boolean;
};
const DiagnosticItemsContainer = ({
	exams = diagnosticItems as unknown as PracticeTestExam[],
	hasAccess,
	handleShowPurchase,
	paddingHorizontal,
	disableAll,
	isDataLoading,
	isSessionConfig,
	handleClose,
}: Props) => {
	const { t } = useTranslation();

	const { isMobile } = useDevice();

	const theme = useAppTheme();
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

	const { handleCreateCommonSession, isCreatingSession } = useSession();
	const [showPermissionDialog, setShowPermissionDialog] = useState(false);
	const [permissionResponse, requestPermission] = Audio.usePermissions();

	const handleCreateSession = async (id: number) => {
		if (diagnosticTagIds.speakingWriting === id) {
			if (permissionResponse?.status !== "granted") {
				const newPermission = await requestPermission();
				if (newPermission.status !== "granted") {
					setShowPermissionDialog(true);
					return;
				}
			}
		}
		setSelectedItemId(id);
		const dist = {
			low: 0,
			high: 0,
			medium: 0,
			none: 0,
		};

		const itemIds =
			diagnosticItems.find((item) => Number(item.id) === id)?.itemIds ?? [];
		const exam = exams?.find((exam) => Number(exam.id) === id);
		handleCreateCommonSession({
			dist,
			tags: [],
			handleClose,
			itemIds,
			push: !isSessionConfig,
			additionalSessionParams: {
				title: exam?.title!,
				icon: exam?.icon!,
				sessionSubType: "DIAGNOSTIC",
				examId: exam?.id,
				// examStatus: exam?.status,
				// sessionId: exam?.numericId,
			},
		});
	};

	const handleViewReport = (sessionNumericId: number) => {
		router.replace({
			pathname: "/(protected)/session/[id]/report",
			params: {
				id: sessionNumericId,
			},
		});
	};

	const handleClickCard = (item: PracticeTestExam) => {
		switch (item.status) {
			case "COMPLETE":
				if (item.numericId) {
					handleViewReport(item.numericId);
				}
				break;
			case "ACTIVE":
				handleCreateSession(Number(item.id));
				break;
			case "NOT_STARTED":
				handleCreateSession(Number(item.id));
				break;
			default:
				break;
		}
	};

	const getIconAndCta = (item: PracticeTestExam) => {
		switch (item.status) {
			case "COMPLETE":
				return {
					icon: "chart-bar" as const,
					cta: t("dashboard.diagnostics.viewReport"),
				};
			case "ACTIVE":
				return {
					icon: "play-circle-outline" as const,
					cta: t("dashboard.diagnostics.resume"),
				};
			default:
				return {
					icon: "play-outline" as const,
					cta: t("dashboard.diagnostics.start"),
				};
		}
	};

	const mappedDiagnosticItems = exams?.map((item) => {
		const numItems = item.numItems;
		const isCompleted = item.status === "COMPLETE";

		const numCompleted = isCompleted ? numItems : item.numAttemptedItems || 0;
		const numRemaining = numItems - numCompleted;
		const timePerItem =
			Number(item.id) === diagnosticTagIds.readingListening ? 2.5 : 5;
		const timeToComplete = Math.round(numRemaining * timePerItem);
		const { icon, cta } = getIconAndCta(item);

		return {
			title: t(`dashboard.diagnostics.${item.title}`),
			icon: item.icon as IconType,
			id: item.id,
			loading: false,
			handleClickCard: () => {
				explicitCallTrackCustomEvent({
					eventName: events.sessionDiagnosticClicked,
					examId: item.id,
					status: item.status,
				});
				handleClickCard(item);
			},
			numCompleted,
			numItems,
			button: {
				cta,
				icon,
				handleClick: () => {
					handleClickCard(item);
				},
			} as const,
			subtitle: isCompleted
				? t("dashboard.diagnostics.completed", {
						count: numCompleted,
						item: smartPluralize("item", numCompleted),
					})
				: t("dashboard.diagnostics.duration", {
						count: numRemaining,
						minutes: timeToComplete,
						item: smartPluralize("item", numRemaining),
					}),
		};
	});

	const chipProps = {
		label: t("dashboard.diagnostics.free"),
		icon: "gift-outline" as const,
		background: theme.palette.chips.free,
	};

	return (
		<Box gap={16}>
			<Box
				justifyContent="space-between"
				alignItems="center"
				flexDirection="row"
				paddingHorizontal={(paddingHorizontal ?? isMobile) ? 16 : 0}
			>
				<CustomListSubheaderContainer enableGutters={false}>
					{t("dashboard.diagnostics.title")}
				</CustomListSubheaderContainer>
				{!hasAccess && (
					<CustomChip
						onPress={handleShowPurchase}
						icon={chipProps.icon}
						style={{
							backgroundColor: chipProps.background,
						}}
						textStyle={{
							color: theme.colors.black,
						}}
					>
						{chipProps.label}
					</CustomChip>
				)}
			</Box>
			<ScrollView
				horizontal
				contentContainerStyle={{
					gap: 8,
					paddingHorizontal: (paddingHorizontal ?? isMobile) ? 16 : 0,
					width: "100%",
					paddingBottom: 1,
				}}
				showsHorizontalScrollIndicator={false}
			>
				{mappedDiagnosticItems?.map((item) => {
					const loading =
						isCreatingSession && selectedItemId === Number(item.id);
					return (
						<Box key={item.title} flex={1} minWidth={180}>
							<DiagnosticCard
								{...item}
								loading={loading}
								disabled={isCreatingSession || disableAll || isDataLoading}
							/>
						</Box>
					);
				})}
			</ScrollView>
			{showPermissionDialog && (
				<MicrophonePermissionDialog
					showDialog={showPermissionDialog}
					setShowDialog={() => {
						setShowPermissionDialog(false);
					}}
				/>
			)}
		</Box>
	);
};

export default DiagnosticItemsContainer;
